import { Component, ElementRef, EventEmitter, Input, OnInit, Output } from '@angular/core'
import { debounceTime } from 'rxjs/operators'
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms'
import { CommonModule } from '@angular/common'
import { TranslateModule, TranslateService } from '@ngx-translate/core'
import { Gallery } from '../../interfaces/api/Gallery'
import { GalleryStatus } from '../../interfaces/api/GalleryStatus'
import { ComponentsModule } from '@components/components.module'
import { ComboboxComponent, Option } from '@components/combobox/combobox.component'
import { ApiService } from '../../services/api.service'
import { LabelValuePair } from '../../interfaces/api/LabelValuePair'
import { GalleryPhotosComponent } from '@components/gallery-photos/gallery-photos.component'
import { GalleryImage } from '../../interfaces/api/GalleryImage'
import { SelectValue } from '../../interfaces/select-value.interface'
import { SelectComponent } from '@components/select/select.component'


@Component({
    selector: 'app-form-newgalleries',
    templateUrl: './form-newgalleries.component.html',
    styleUrl: './form-newgalleries.component.scss',
    standalone: true,
    imports: [CommonModule, TranslateModule, FormsModule, ReactiveFormsModule, ComponentsModule, ComboboxComponent, GalleryPhotosComponent, SelectComponent]
})
export class FormNewgalleriesComponent implements OnInit {

    categories: string[];
    spaces: string[];
    collaborators: LabelValuePair<string>[]
    @Input({ required: true }) creating: boolean
    @Input() gallery: Gallery
    @Output() galleryImageChange = new EventEmitter<GalleryImage>()
    @Output() formStatusChange = new EventEmitter<boolean>()

    selectedCategory: string|null = null
    selectedSpace: string|null = null
    selectedCollab: string|null = null

    formGallery: FormGroup

    styles: string[] = []

    application: {
        label: string;
        options: { value: string; label: string; }[];
    }
    brand: {
        label: string;
        options: { value: string; label: string; }[];
    }
    color: {
        label: string;
        options: { value: string; label: string; }[];
    }

    selectedStyle: string | null = null
    uploadedImages: GalleryImage[] = []

    descriptionCharacterCount: number = 0;

    galleryPhotosValid = false;

    categoryOptions: SelectValue[] = [];
    spaceOptions: SelectValue[] = []
    collaboratorOptions: SelectValue[] = []

    constructor(private eRef: ElementRef, private fb: FormBuilder, private api: ApiService, private translate: TranslateService) {

    }

    ngOnInit(): void {
        this.api.myGalleries__categories().subscribe({
            next: categories => {
                this.categories = categories;
                this.categoryOptions = this.getCategories()
            }
        });
        this.api.myGalleries__spaces().subscribe({
            next: spaces => {
                this.spaces = spaces;
                this.spaceOptions = this.getSpaces()
            }
        });
        this.api.myGalleries__styles().subscribe(styles => this.styles = styles);
        this.api.myGalleries__collaborators(false).subscribe({
            next: collaborators => {
                this.collaborators = collaborators;
                this.collaboratorOptions = this.getCollaborators()
            }
        });

        this.formGallery = this.fb.group({
            galleryName: ['', Validators.required],
            description: ['', [Validators.required, Validators.minLength(24), Validators.maxLength(640)]],
            category: ['', Validators.required],
            space: ['', Validators.required],
            collaborator: [''],
            style: [''],
            images: [[]],
            statusGallery: [GalleryStatus.PENDING_APPROVAL]
        })


        const descriptionControl = this.formGallery.get('description');
        descriptionControl?.valueChanges.subscribe(value => {
            this.descriptionCharacterCount = value ? value.length : 0;
            descriptionControl.markAsUntouched({ onlySelf: true });
        });

        this.formGallery.valueChanges.subscribe(() => {
            this.formStatusChange.emit(this.isFormValid())
        })

        if (this.gallery) {
            this.formGallery.patchValue({
                galleryName: this.gallery.name,
                category: this.gallery.category,
                space: this.gallery.space,
                collaborator: this.gallery.fabricator,
                description: this.gallery.description,
                style: this.gallery.style
            })

            this.selectedCategory = this.gallery.category
            this.selectedSpace = this.gallery.space
            this.selectedCollab = this.gallery.fabricator || null
            this.selectedStyle = this.gallery.style
        }

        // Registrado después de patchValue para que no dispare en la carga inicial
        descriptionControl?.valueChanges.pipe(debounceTime(800)).subscribe((value) => {
            if (value && value.length > 0) {
                descriptionControl.markAsTouched({ onlySelf: true });
            }
        });


        if (!this.creating) {
            this.formGallery.disable()
        }
    }

    getCategories(): SelectValue[] {
        return this.categories.map(category => {
            return {
                id: category,
                value: category,
                label: "gallery-category." + category,
            };
        });
    }

    getSpaces(): SelectValue[] {
        return this.spaces.map(space => {
            return {
                id: space,
                value: space,
                label: "gallery-space." + space,
            };
        });
    }

    getCollaborators(): SelectValue[] {
        return this.collaborators.map(collaborator => {
            return {
                id: collaborator.value,
                value: collaborator.value,
                label: collaborator.label,
            };
        });
    }

    selectStyle(styleId: string): void {
        this.selectedStyle = styleId
        this.formGallery.get('style')?.setValue(styleId)

        console.log('Estilo seleccionado:', this.selectedStyle)
    }

    getFormData(): Gallery | null {
        if (!this.isFormValid()) return null

        const formValues = this.formGallery.value

        return {
            name: formValues.galleryName,
            description: formValues.description,
            category: formValues.category,
            space: formValues.space,
            fabricator: formValues.collaborator,
            style: this.selectedStyle!,
            images: this.uploadedImages ?? [],
            status: GalleryStatus.PENDING_APPROVAL
        }

    }

    selectCategory(category: SelectValue | null): void {
        this.selectedCategory = category?.value
        this.formGallery.get('category')?.setValue(category?.value)
    }

    getSelectedCategory(): SelectValue | null {
        const category = this.selectedCategory;
        if (category) {
            return this.categoryOptions.find(categoryValue => categoryValue.value === category) || null;
        }
        return null;
    }

    selectSpace(space: SelectValue | null): void {
        this.selectedSpace = space?.value
        this.formGallery.get('space')?.setValue(space?.value)
    }

    getSelectedSpace(): SelectValue | null {
        const space = this.selectedSpace;
        if (space) {
            return this.spaceOptions.find(spaceValue => spaceValue.value === space) || null;
        }
        return null;
    }

    selectCollabs(collaborator: SelectValue | null): void {
        this.selectedCollab = collaborator?.value
        this.formGallery.get('collaborator')?.setValue(collaborator?.value)
    }

    getSelectedCollab(): SelectValue | null {
        const collab = this.selectedCollab;
        if (collab) {
            return this.collaboratorOptions.find(collabValue => collabValue.value === collab) || null;
        }
        return null;
    }

    onImagesUploaded(images: GalleryImage[]): void {
        this.uploadedImages = images
        this.formGallery.get('images')?.setValue(images)
    }

    isFormValid(): boolean {
        return this.formGallery.valid
          && this.selectedStyle !== null
          && this.uploadedImages.length > 0
          && this.galleryPhotosValid;
    }

    onDescriptionBlur() {
        const control = this.formGallery.get('description');
        const value = control?.value;
        if (value && value.length > 0) {
            control?.markAsTouched({ onlySelf: true });
        }
    }

    onGalleryPhotosValidate(valid: boolean) {
        this.galleryPhotosValid = valid;
        this.formStatusChange.emit(this.isFormValid());
    }
}
